import { Text } from '@mantine/core';
import type { ColumnDef } from '@tanstack/react-table';
import { DateCell } from '@ayocore/platform/client/components/RTable/cells';
import { UserCell } from '@ayocore/platform/identity/client/cells';
import type { Folder } from '../../../schemas/folder.schema';
import type { UploadedFile } from '../../../schemas/uploaded_file.schema';
import { DriveNameCell } from './DriveNameCell';
import { FileSizeCell } from './FileSizeCell';
import { FolderActionsMenu } from './FolderActionsMenu';
import { isFolderRow, pickAuthor } from './driveRow';
import { resolveEntryLocation, type DriveLocationMode } from './driveScope';

/** Обработчики меню действий папки — колонка появляется только вместе с ними. */
export interface DriveFolderActionsConfig {
  currentUserId: number | null;
  isAdmin: boolean;
  onCreateChild: (parentFolderId: number) => void;
  onRename: (folder: Folder) => void;
  onMove: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onChangeAccess: (folder: Folder) => void;
  /** Перечитать Диск — нужен доменным пунктам меню (defineDriveActions). */
  onRefresh: () => void;
}

export interface BuildDriveColumnsOptions {
  /** Папки по id — цвет иконки папки (эффективная зона), путь и меню действий. */
  foldersById: Map<number, Folder>;
  /** Режим показа пути к папке (по умолчанию `never`). */
  location?: DriveLocationMode;
  /** Активная папка — нужна режиму `foreign`, чтобы понять, «чужая» ли строка. */
  activeFolderId?: number | null;
  /**
   * Меню действий папки. Не задано — колонки действий нет: так read-only
   * экраны (пикер, поиск, встраиваемая панель) физически не могут показать
   * мутации, вместо того чтобы прятать их флагом.
   */
  folderActions?: DriveFolderActionsConfig;
}

/**
 * Колонки списка Диска — единственный источник правды для ВСЕХ экранов:
 * страницы Диска, встраиваемой панели, файл-пикера и модалки поиска.
 *
 * Раньше каждый экран объявлял свои четыре колонки заново, и они разъехались:
 * где-то «Создано», где-то «Загружено», путь к папке то отдельной колонкой, то
 * строкой под именем. Здесь набор один, а различия сведены к двум опциям —
 * показу пути (`location`) и наличию меню действий (`folderActions`).
 *
 * Сортировка: по имени, размеру и дате — серверная (колонки реальные);
 * автор не сортируется (виртуальная колонка `withCreatedBy`).
 *
 * @example
 * ```tsx
 * // страница Диска: путь у чужих строк + меню действий
 * buildDriveColumns({ foldersById, location: 'foreign', activeFolderId, folderActions });
 * // модалка поиска: путь всегда, мутаций нет
 * buildDriveColumns({ foldersById, location: 'always' });
 * ```
 */
export function buildDriveColumns({
  foldersById,
  location = 'never',
  activeFolderId,
  folderActions,
}: BuildDriveColumnsOptions): ColumnDef<UploadedFile>[] {
  const columns: ColumnDef<UploadedFile>[] = [
    {
      id: 'original_name',
      header: 'Имя',
      accessorKey: 'original_name',
      enableSorting: true,
      cell: ({ row }) => (
        <DriveNameCell
          row={row.original}
          foldersById={foldersById}
          location={resolveEntryLocation(row.original, {
            foldersById,
            mode: location,
            activeFolderId,
          })}
        />
      ),
    },
    {
      id: 'file_size',
      header: 'Размер',
      accessorKey: 'file_size',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        if (!isFolderRow(row.original)) {
          return <FileSizeCell value={row.original.file_size} />;
        }
        // У папки размера нет
        return <Text c='dimmed'>—</Text>;
      },
    },
    {
      id: 'created_by',
      header: 'Кем создано',
      accessorKey: 'created_by_id',
      size: 180,
      // Виртуальная колонка (withCreatedBy) — серверной сортировки по ней нет
      enableSorting: false,
      cell: ({ row }) => <UserCell user={pickAuthor(row.original)} fallback='—' />,
    },
    {
      id: 'created_at',
      header: 'Создано',
      accessorKey: 'created_at',
      size: 140,
      enableSorting: true,
      cell: ({ row }) => <DateCell value={row.original.created_at} />,
    },
  ];

  if (folderActions) {
    columns.push({
      id: 'actions',
      header: '',
      size: 48,
      enableSorting: false,
      cell: ({ row }) => {
        // Меню есть только у папок: файлами управляют массовые действия тулбара
        if (!isFolderRow(row.original)) return null;
        const folder = foldersById.get(row.original.id);
        if (!folder) return null;
        return (
          <FolderActionsMenu
            folder={folder}
            foldersById={foldersById}
            currentUserId={folderActions.currentUserId}
            isAdmin={folderActions.isAdmin}
            onCreateChild={folderActions.onCreateChild}
            onRename={folderActions.onRename}
            onMove={folderActions.onMove}
            onDelete={folderActions.onDelete}
            onChangeAccess={folderActions.onChangeAccess}
            onRefresh={folderActions.onRefresh}
          />
        );
      },
    });
  }

  return columns;
}
